import React, { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Upload,
  FileText,
  Eye,
  FolderOpen,
  File,
  FileCheck,
  Shield,
  FileEdit,
  FileBadge,
  Loader2,
  AlertCircle,
  X,
  Archive,
  RotateCcw,
} from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import {
  GET_DOCUMENTS,
  REQUEST_UPLOAD_URL,
  CONFIRM_UPLOAD,
  REQUEST_DOWNLOAD_URL,
  ARCHIVE_DOCUMENT,
  UNARCHIVE_DOCUMENT,
  uploadToS3,
  formatFileSize,
  CATEGORY_LABELS,
  type DocumentCategory,
  type GetDocumentsData,
  type GetDocumentsVariables,
  type RequestUploadUrlData,
  type ConfirmUploadData,
  type RequestDownloadUrlData,
  type ArchiveDocumentData,
  type UnarchiveDocumentData,
} from '@/graphql/documents';

// Map GraphQL category to lowercase for styling
type StyleCategory = 'contract' | 'sow' | 'nda' | 'amendment' | 'insurance' | 'other';

const CATEGORY_ICONS: Record<StyleCategory, typeof FileText> = {
  contract: FileCheck,
  sow: FileText,
  nda: Shield,
  amendment: FileEdit,
  insurance: FileBadge,
  other: File,
};

const CATEGORY_STYLES: Record<StyleCategory, string> = {
  contract: 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua',
  sow: 'bg-pond/10 text-pond dark:bg-pond-light/20 dark:text-pond-light',
  nda: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light',
  amendment: 'bg-orange/10 text-orange dark:bg-orange/20 dark:text-orange-light',
  insurance: 'bg-yellow/10 text-earth dark:bg-yellow-light/20 dark:text-yellow-light',
  other: 'badge-neutral',
};

// Accepted file types
const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/png',
  'image/jpeg',
  'text/plain',
];

interface FilesTabProps {
  clientId: string;
}

export function FilesTab({ clientId }: FilesTabProps) {
  // State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showArchived, setShowArchived] = useState(false);
  
  // Upload form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory>('CONTRACT');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // GraphQL Query
  const { data, loading, error, refetch } = useQuery<GetDocumentsData, GetDocumentsVariables>(
    GET_DOCUMENTS,
    {
      variables: { 
        organizationId: clientId,
        category: filterCategory !== 'all' ? filterCategory as DocumentCategory : undefined,
        includeArchived: showArchived,
      },
      fetchPolicy: 'cache-and-network',
    }
  );
  
  // GraphQL Mutations
  const [requestUploadUrl] = useMutation<RequestUploadUrlData>(REQUEST_UPLOAD_URL);
  const [confirmUpload] = useMutation<ConfirmUploadData>(CONFIRM_UPLOAD);
  const [requestDownloadUrl] = useMutation<RequestDownloadUrlData>(REQUEST_DOWNLOAD_URL);
  const [archiveDocument] = useMutation<ArchiveDocumentData>(ARCHIVE_DOCUMENT);
  const [unarchiveDocument] = useMutation<UnarchiveDocumentData>(UNARCHIVE_DOCUMENT);

  const documents = data?.documents.items || [];
  
  // Filter documents based on category (already filtered by GraphQL if category is set)
  const filteredFiles = filterCategory === 'all' 
    ? documents 
    : documents;

  // Group files by category for summary (only non-archived)
  const filesByCategory = documents.filter(f => !f.isArchived).reduce((acc, file) => {
    const cat = file.category.toLowerCase();
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Count archived files
  const archivedCount = documents.filter(f => f.isArchived).length;

  // Reset upload form
  const resetUploadForm = useCallback(() => {
    setSelectedFile(null);
    setUploadName('');
    setUploadCategory('CONTRACT');
    setUploadProgress(0);
    setUploadError(null);
    setIsUploading(false);
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((file: File) => {
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setUploadError('Invalid file type. Please upload a PDF, Word, Excel, or image file.');
      return;
    }
    
    setSelectedFile(file);
    setUploadError(null);
    
    // Auto-fill name from filename (without extension)
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    setUploadName(nameWithoutExt);
  }, []);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // Open file picker
  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Step 1: Request presigned upload URL
      const { data: urlData } = await requestUploadUrl({
        variables: {
          input: {
            organizationId: clientId,
            filename: selectedFile.name,
            category: uploadCategory,
            contentType: selectedFile.type,
          },
        },
      });

      if (!urlData?.requestUploadUrl) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, s3Key } = urlData.requestUploadUrl;

      // Step 2: Upload file to S3
      await uploadToS3(uploadUrl, selectedFile, (progress) => {
        setUploadProgress(progress);
      });

      // Step 3: Confirm upload and save metadata
      await confirmUpload({
        variables: {
          input: {
            organizationId: clientId,
            name: uploadName || selectedFile.name,
            originalFilename: selectedFile.name,
            category: uploadCategory,
            s3Key,
            mimeType: selectedFile.type,
            sizeBytes: selectedFile.size,
          },
        },
      });

      // Success - close modal and refresh
      setShowUploadModal(false);
      resetUploadForm();
      refetch();
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle view file - opens in new tab
  const handleViewFile = async (documentId: string) => {
    try {
      const { data } = await requestDownloadUrl({
        variables: { documentId },
      });

      if (data?.requestDownloadUrl?.downloadUrl) {
        // Open file in new tab for viewing
        window.open(data.requestDownloadUrl.downloadUrl, '_blank');
      }
    } catch (err) {
      console.error('View file failed:', err);
      alert('Failed to open file. Please try again.');
    }
  };

  // Handle archive
  const handleArchive = async (documentId: string) => {
    try {
      await archiveDocument({
        variables: { id: documentId },
      });
      setShowArchiveConfirm(null);
      refetch();
    } catch (err) {
      console.error('Archive failed:', err);
      alert('Failed to archive file. Please try again.');
    }
  };

  // Handle unarchive
  const handleUnarchive = async (documentId: string) => {
    try {
      await unarchiveDocument({
        variables: { id: documentId },
      });
      refetch();
    } catch (err) {
      console.error('Unarchive failed:', err);
      alert('Failed to restore file. Please try again.');
    }
  };

  // Helper to get style category from GraphQL category
  const getStyleCategory = (category: DocumentCategory): StyleCategory => {
    return category.toLowerCase() as StyleCategory;
  };

  if (error) {
    return (
      <div className="p-8 text-center text-rust">
        <AlertCircle className="w-12 h-12 mx-auto mb-3" />
        <p>Error loading documents: {error.message}</p>
        <button onClick={() => refetch()} className="btn-secondary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate dark:text-mist">Files</h2>
          <p className="text-sm text-chalkboard dark:text-rain-cloud">
            Contracts, SOWs, and other client documents
          </p>
        </div>
        <button onClick={() => setShowUploadModal(true)} className="btn-primary">
          <Upload className="w-4 h-4" />
          Upload File
        </button>
      </div>

      {/* File Category Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {Object.entries(CATEGORY_LABELS).map(([category, label]) => {
          const styleCategory = category.toLowerCase() as StyleCategory;
          const Icon = CATEGORY_ICONS[styleCategory];
          const count = filesByCategory[styleCategory] || 0;
          return (
            <button
              key={category}
              onClick={() => setFilterCategory(filterCategory === category ? 'all' : category)}
              className={cn(
                'card cursor-pointer transition-all hover:shadow-md',
                filterCategory === category && 'ring-2 ring-pond dark:ring-pond-light'
              )}
            >
              <div className="card-body py-3 px-4">
                <div className="flex items-center gap-2">
                  <Icon className={cn(
                    'w-4 h-4',
                    CATEGORY_STYLES[styleCategory].includes('text-') 
                      ? CATEGORY_STYLES[styleCategory].split(' ').find(c => c.startsWith('text-'))
                      : 'text-chalkboard dark:text-rain-cloud'
                  )} />
                  <span className="text-xs font-medium text-chalkboard dark:text-rain-cloud truncate">
                    {label}
                  </span>
                </div>
                <p className="text-xl font-semibold text-slate dark:text-mist mt-1">{count}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* Category filter indicator */}
        {filterCategory !== 'all' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-chalkboard dark:text-rain-cloud">
              Showing: <span className="font-medium text-slate dark:text-mist">{CATEGORY_LABELS[filterCategory as DocumentCategory]}</span>
            </span>
            <button 
              onClick={() => setFilterCategory('all')}
              className="text-sm text-pond dark:text-pond-light hover:underline"
            >
              Clear filter
            </button>
          </div>
        )}
        
        {/* Show archived toggle */}
        <div className="flex items-center gap-2 ml-auto">
          <label className="flex items-center gap-2 text-sm text-chalkboard dark:text-rain-cloud cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-gray dark:border-night-pond text-pond focus:ring-pond"
            />
            <Archive className="w-4 h-4" />
            Show archived {archivedCount > 0 && `(${archivedCount})`}
          </label>
        </div>
      </div>

      {/* Files Table */}
      <div className="card overflow-hidden">
        {loading && documents.length === 0 ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-pond" />
            <p className="text-chalkboard dark:text-rain-cloud">Loading documents...</p>
          </div>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Category</th>
                  <th>Upload Date</th>
                  <th>Uploaded By</th>
                  <th>Size</th>
                  <th className="w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((doc) => {
                  const styleCategory = getStyleCategory(doc.category);
                  const CategoryIcon = CATEGORY_ICONS[styleCategory];
                  const isArchived = doc.isArchived;
                  return (
                    <tr key={doc.id} className={cn('group', isArchived && 'opacity-60')}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center',
                            CATEGORY_STYLES[styleCategory].split(' ').slice(0, 2).join(' ')
                          )}>
                            <CategoryIcon className="w-4 h-4" />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate dark:text-mist">{doc.name}</span>
                            {isArchived && (
                              <span className="badge badge-neutral text-xs">
                                <Archive className="w-3 h-3 mr-1" />
                                Archived
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={cn('badge', CATEGORY_STYLES[styleCategory])}>
                          {CATEGORY_LABELS[doc.category]}
                        </span>
                      </td>
                      <td className="text-chalkboard dark:text-rain-cloud">
                        {formatDate(new Date(doc.createdAt))}
                      </td>
                      <td className="text-chalkboard dark:text-rain-cloud">{doc.uploadedBy}</td>
                      <td className="text-chalkboard dark:text-rain-cloud">{formatFileSize(doc.sizeBytes)}</td>
                      <td>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => handleViewFile(doc.id)}
                            className="btn-ghost btn-icon btn-sm"
                            title="View file"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {isArchived ? (
                            <button 
                              onClick={() => handleUnarchive(doc.id)}
                              className="btn-ghost btn-icon btn-sm text-turquoise dark:text-aqua"
                              title="Restore file"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          ) : (
                            <button 
                              onClick={() => setShowArchiveConfirm(doc.id)}
                              className="btn-ghost btn-icon btn-sm text-orange dark:text-orange-light"
                              title="Archive file"
                            >
                              <Archive className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredFiles.length === 0 && (
              <div className="p-8 text-center text-chalkboard dark:text-rain-cloud">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No files found. {filterCategory !== 'all' && 'Try clearing the filter or '}Upload a file to get started.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => { setShowUploadModal(false); resetUploadForm(); }}
        title="Upload File"
      >
        <div className="space-y-4">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
          />
          
          {/* Drag and drop zone */}
          <div 
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={openFilePicker}
            className={cn(
              'p-6 border-2 border-dashed rounded-lg text-center cursor-pointer transition-all',
              isDragging 
                ? 'border-pond bg-pond/10 dark:border-pond-light dark:bg-pond-light/10' 
                : 'border-gray dark:border-night-pond hover:border-pond dark:hover:border-pond-light',
              selectedFile && 'border-turquoise bg-turquoise/5 dark:border-aqua dark:bg-aqua/5'
            )}
          >
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileCheck className="w-8 h-8 text-turquoise dark:text-aqua" />
                <div className="text-left">
                  <p className="font-medium text-slate dark:text-mist">{selectedFile.name}</p>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">
                    {formatFileSize(selectedFile.size)}
                  </p>
                </div>
                <button 
                  onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                  className="ml-2 p-1 hover:bg-gray/20 rounded"
                >
                  <X className="w-4 h-4 text-chalkboard" />
                </button>
              </div>
            ) : (
              <>
                <Upload className={cn(
                  'w-10 h-10 mx-auto mb-3',
                  isDragging ? 'text-pond dark:text-pond-light' : 'text-chalkboard dark:text-rain-cloud'
                )} />
                <p className="text-sm text-chalkboard dark:text-rain-cloud mb-1">
                  {isDragging ? 'Drop file here' : 'Drag and drop a file here, or click to browse'}
                </p>
                <p className="text-xs text-chalkboard/60 dark:text-rain-cloud/60">
                  PDF, Word, Excel, or images up to 50MB
                </p>
              </>
            )}
          </div>

          {/* Upload progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-chalkboard dark:text-rain-cloud">Uploading...</span>
                <span className="text-slate dark:text-mist font-medium">{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-gray/20 dark:bg-night-pond rounded-full overflow-hidden">
                <div 
                  className="h-full bg-pond dark:bg-pond-light transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error message */}
          {uploadError && (
            <div className="p-3 bg-rust/10 border border-rust/20 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-rust flex-shrink-0 mt-0.5" />
              <p className="text-sm text-rust">{uploadError}</p>
            </div>
          )}

          {/* File name input */}
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder="Enter display name for the file"
              className="input"
              disabled={isUploading}
            />
          </div>

          {/* Category select */}
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Category
            </label>
            <select
              value={uploadCategory}
              onChange={(e) => setUploadCategory(e.target.value as DocumentCategory)}
              className="select"
              disabled={isUploading}
            >
              {Object.entries(CATEGORY_LABELS).map(([category, label]) => (
                <option key={category} value={category}>{label}</option>
              ))}
            </select>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button 
              onClick={() => { setShowUploadModal(false); resetUploadForm(); }}
              className="btn-secondary"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button 
              onClick={handleUpload} 
              className="btn-primary"
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal
        isOpen={!!showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(null)}
        title="Archive File"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            Are you sure you want to archive this file? You can restore it later from the archived files.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowArchiveConfirm(null)} className="btn-secondary">
              Cancel
            </button>
            <button 
              onClick={() => showArchiveConfirm && handleArchive(showArchiveConfirm)}
              className="btn-primary bg-orange hover:bg-orange/90"
            >
              <Archive className="w-4 h-4" />
              Archive
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
